import { FormEvent, useEffect, useReducer, useState } from 'react';
import { IGroup, IListItem } from '@nkr/models/index';
import { IGroupData } from '@nkr/store/index';
import { Button, Col, ControlLabel, FormControl, FormGroup, Modal, Radio } from 'react-bootstrap'
import { GroupFormWrapper } from '../styles';
import { addEditReducer, initialState } from './state';

interface IProps {
    show: boolean,
    data: IGroup,
    ticketGroupList: IListItem[],
    onSave: (data: IGroupData) => void,
    onCancel: () => void
}

const GroupAddEdit = (props: IProps) => {
    const [state, dispatch] = useReducer(addEditReducer, initialState);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (props.data != null) {
            dispatch({
                type: 'loadData',
                payload: props.data
            });
        } else {
            dispatch({
                type: 'initialize'
            });
        }

        setDirty(false);
    }, [props.data]);

    useEffect(() => {
        if (dirty) {
            let isValid = state.validation.name && state.validation.description;

            if (isValid) {
                let { id, name, description, isSuperAdmin, canUseCp, canManageUsers, canChangeUsername, canChangePassword,
                    canEditProfile, isCenter, canAddCenterUser, isLogisticsProvider, isXmKitVendor, isSerologyVendor,
                    isRecipient, isDonor, isPhlebotomist, isNkrLab, canAccessTicketing, accessTicketGroup,
                    isNkdo
                } = state;

                props.onSave({
                    id,
                    name,
                    description,
                    isSuperAdmin,
                    canUseCp,
                    canManageUsers, canChangeUsername, canChangePassword,
                    canEditProfile, isCenter, canAddCenterUser, isLogisticsProvider, isXmKitVendor, isSerologyVendor,
                    isRecipient, isDonor, isPhlebotomist, isNkrLab, canAccessTicketing, accessTicketGroup,
                    isNkdo
                });
            }
        }
    }, [dirty, state]);

    const handleTextFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLTextAreaElement>) => {
        let { name, value } = e.currentTarget

        dispatch({
            type: 'update',
            payload: {
                name,
                value
            }
        });
    };

    const handleSelectFieldChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget

        dispatch({
            type: 'update',
            payload: {
                name,
                value: parseInt(value)
            }
        });
    };

    const handleRadioChange = (e: FormEvent<Radio & HTMLInputElement>) => {
        let { name, value } = e.currentTarget

        dispatch({
            type: 'update',
            payload: {
                name,
                value: parseInt(value) == 1
            }
        });
    };

    const handleSave = () => {
        setDirty(true);

        dispatch({
            type: 'validate'
        });
    }

    return (
        <Modal backdrop='static' show={props.show} onHide={props.onCancel}>
            <Modal.Header closeButton>{props.data != null ? 'Edit Group' : 'Add Group'}</Modal.Header>
            <Modal.Body>
                <GroupFormWrapper horizontal>
                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Name:</Col>
                        <Col xs={12} lg={8}>
                            <FormControl
                                name='name'
                                value={state.name}
                                onChange={handleTextFieldChange} />
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Description:</Col>
                        <Col xs={12} lg={8}>
                            <FormControl
                                componentClass={'textarea'}
                                rows={2}
                                style={{ resize: 'none' }}
                                name='description'
                                value={state.description}
                                onChange={handleTextFieldChange} />
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Is Super Admin?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='isSuperAdmin' value={1} checked={state.isSuperAdmin} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='isSuperAdmin' value={0} checked={!state.isSuperAdmin} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Can Manage Users?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='canManageUsers' value={1} checked={state.canManageUsers} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='canManageUsers' value={0} checked={!state.canManageUsers} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Can Use CP?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='canUseCp' value={1} checked={state.canUseCp} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='canUseCp' value={0} checked={!state.canUseCp} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Can Access Ticketing?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='canAccessTicketing' value={1} checked={state.canAccessTicketing} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='canAccessTicketing' value={0} checked={!state.canAccessTicketing} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Access Ticket Group:</Col>
                        <Col xs={12} lg={8}>
                            <FormControl
                                componentClass='select'
                                name='accessTicketGroup'
                                value={state.accessTicketGroup}
                                onChange={handleSelectFieldChange}
                            >
                                <option value={0}>- No Access -</option>
                                <option value={-1}>- All Tickets -</option>
                                {props.ticketGroupList.map(item =>
                                    <option key={'tg-' + item.id} value={item.id}>{item.description}</option>
                                )}
                            </FormControl>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Can Change Username?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='canChangeUsername' value={1} checked={state.canChangeUsername} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='canChangeUsername' value={0} checked={!state.canChangeUsername} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Can Change Password?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='canChangePassword' value={1} checked={state.canChangePassword} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='canChangePassword' value={0} checked={!state.canChangePassword} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Can Edit Profile?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='canEditProfile' value={1} checked={state.canEditProfile} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='canEditProfile' value={0} checked={!state.canEditProfile} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Is Center?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='isCenter' value={1} checked={state.isCenter} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='isCenter' value={0} checked={!state.isCenter} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Is NKDO?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='isNkdo' value={1} checked={state.isNkdo} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='isNkdo' value={0} checked={!state.isNkdo} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Can Add Center User?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='canAddCenterUser' value={1} checked={state.canAddCenterUser} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='canAddCenterUser' value={0} checked={!state.canAddCenterUser} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Is Logistics Provider?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='isLogisticsProvider' value={1} checked={state.isLogisticsProvider} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='isLogisticsProvider' value={0} checked={!state.isLogisticsProvider} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Is XM Kit Vendor?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='isXmKitVendor' value={1} checked={state.isXmKitVendor} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='isXmKitVendor' value={0} checked={!state.isXmKitVendor} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Is Serology Vendor?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='isSerologyVendor' value={1} checked={state.isSerologyVendor} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='isSerologyVendor' value={0} checked={!state.isSerologyVendor} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Is Recipient?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='isRecipient' value={1} checked={state.isRecipient} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='isRecipient' value={0} checked={!state.isRecipient} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Is Donor?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='isDonor' value={1} checked={state.isDonor} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='isDonor' value={0} checked={!state.isDonor} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Is Phlebotomist?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='isPhlebotomist' value={1} checked={state.isPhlebotomist} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='isPhlebotomist' value={0} checked={!state.isPhlebotomist} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>

                    <FormGroup>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Is NKR Lab?</Col>
                        <Col xs={12} lg={8}>
                            <Radio inline name='isNkrLab' value={1} checked={state.isNkrLab} onChange={handleRadioChange}>Yes</Radio>
                            <Radio inline name='isNkrLab' value={0} checked={!state.isNkrLab} onChange={handleRadioChange}>No</Radio>
                        </Col>
                    </FormGroup>
                </GroupFormWrapper>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle='primary' onClick={handleSave}>Save</Button>
                <Button onClick={props.onCancel}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default GroupAddEdit;